import { useContext, useMemo } from 'react';

import { ProgressBar, Tooltip } from '@smartshell/ui-kit';
import cn from 'classnames';
import { Column } from 'react-table';

import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';

import { IData, EType } from '../interface';
import s from './TableBlock.module.scss';

export const useColumns = (
  type: EType,
  data: IData[]
): Array<Column<IData>> => {
  const { t } = useContext(LocalizationContext);
  const size = useWindowResize();

  const totalNumberLaunches = useMemo(() => {
    return data.reduce((sum, row) => sum + row.numberLaunches, 0);
  }, [data, type]);

  const maxTimeLength = useMemo(() => {
    return Math.max(
      ...data.map((row) =>
        Math.floor(row.time / 3600) > 0
          ? `${Math.floor(row.time / 3600)} ч.`.length
          : '<1 ч.'.length
      )
    );
  }, [data]);

  const timeWidth = useMemo(() => `${maxTimeLength}ch`, [maxTimeLength]);

  return useMemo<Array<Column<IData>>>(() => {
    const baseColumns = [
      {
        id: 'title',
        accessor: 'title',
        Header: t('title'),
        width: size.width > 960 ? 319 : 150,
        disableResizing: true,
      },
      ...(size.width > 960
        ? [
            {
              id: 'percent',
              Header: false,
              accessor: 'percent',
              className: s.percent,
              Cell: ({
                row: {
                  original: { percent, title, time, numberLaunches },
                },
              }: {
                row: {
                  original: Pick<
                    IData,
                    'percent' | 'title' | 'time' | 'numberLaunches'
                  >;
                };
              }) => (
                <Tooltip
                  className={s.tooltip}
                  contentClassName={s.tooltip_content}
                  content={
                    <div className={s.tooltip_block}>
                      <span className={s.tooltip_title}>{title}</span>
                      <div className={s.time_block}>
                        {type !== 'CLICK' ? (
                          <>
                            <span className={s.time_title}>
                              {t('applicationsReport.table.time')}
                            </span>
                            <span className={s.time_count}>
                              {Math.floor(time / 3600) > 0
                                ? `${Math.floor(time / 3600)} ч.`
                                : '<1 ч.'}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className={s.time_title}>
                              {t('applicationsReport.table.count')}
                            </span>
                            <span className={s.time_count}>
                              {numberLaunches}
                            </span>
                          </>
                        )}
                      </div>
                      <div className={s.percent_block}>
                        {type !== 'CLICK' ? (
                          <>
                            <span className={s.percent_title}>
                              {t('report.hosts.inPercents')}
                            </span>
                            <span className={s.percent_count}>
                              {percent < 0.01
                                ? '< 0.01%'
                                : `${percent
                                    .toFixed(2)
                                    .replace(/\.?0+$/, '')} %`}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className={s.percent_title}>
                              {t('applicationsReport.table.count.total')}
                            </span>
                            <span className={s.percent_count}>
                              {totalNumberLaunches}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  }
                >
                  <ProgressBar
                    className={s.progressBar}
                    total={type !== 'CLICK' ? 100 : totalNumberLaunches}
                    used={type !== 'CLICK' ? percent : numberLaunches}
                  />
                </Tooltip>
              ),
              disableResizing: false,
            },
          ]
        : []),
      {
        id: 'time',
        accessor: 'time',
        Header:
          type === EType.CLICK
            ? t('applicationsReport.table.count')
            : t('applicationsReport.table.time'),
        Cell: ({
          row: {
            original: { percent, time, numberLaunches },
          },
        }: {
          row: {
            original: Pick<IData, 'percent' | 'time' | 'numberLaunches'>;
          };
        }) => (
          <div className={cn(s.right, size.width < 960 && s.mobile)}>
            {type !== 'CLICK' ? (
              <>
                <span style={{ width: timeWidth }} className={s.right_time}>
                  {Math.floor(time / 3600) > 0
                    ? `${Math.floor(time / 3600)} ч.`
                    : '<1 ч.'}
                </span>
                <span className={s.right_percent}>
                  {percent < 0.01
                    ? '< 0.01%'
                    : `${percent.toFixed(2).replace(/\.?0+$/, '')} %`}
                </span>
              </>
            ) : (
              <span className={s.right_time}>{numberLaunches}</span>
            )}
          </div>
        ),
        width: size.width > 960 ? 319 : 160,
        disableResizing: true,
      },
    ];

    return baseColumns;
  }, [t, type, data, size.width]);
  // return useMemo<Array<Column<IData>>>(
  //   () => [
  //     {
  //       id: 'title',
  //       accessor: 'title',
  //       Header: t('title'),
  //       width: size.width > 960 ? 319 : 148,
  //       disableResizing: true,
  //     },
  //     {
  //       id: 'percent',
  //       Header: false,
  //       accessor: 'percent',
  //       className: s.percent,
  //       Cell: ({
  //         row: {
  //           original: { percent, title, time, numberLaunches },
  //         },
  //       }: {
  //         row: {
  //           original: Pick<
  //             IData,
  //             'percent' | 'title' | 'time' | 'numberLaunches'
  //           >;
  //         };
  //       }) => (
  //         <Tooltip
  //           className={s.tooltip}
  //           contentClassName={s.tooltip_content}
  //           content={
  //             <div className={s.tooltip_block}>
  //               <span className={s.tooltip_title}>{title}</span>
  //               <div className={s.time_block}>
  //                 {type !== 'CLICK' ? (
  //                   <>
  //                     <span className={s.time_title}>
  //                       {t('applicationsReport.table.time')}
  //                     </span>
  //                     <span className={s.time_count}>
  //                       {Math.floor(time / 3600) > 0
  //                         ? `${Math.floor(time / 3600)} ч.`
  //                         : '<1 ч.'}
  //                     </span>
  //                   </>
  //                 ) : (
  //                   <>
  //                     <span className={s.time_title}>
  //                       {t('applicationsReport.table.count')}
  //                     </span>
  //                     <span className={s.time_count}>{numberLaunches}</span>
  //                   </>
  //                 )}
  //               </div>
  //               <div className={s.percent_block}>
  //                 {type !== 'CLICK' ? (
  //                   <>
  //                     <span className={s.percent_title}>
  //                       {t('report.hosts.inPercents')}
  //                     </span>
  //                     <span className={s.percent_count}>
  //                       {percent < 0.01
  //                         ? '< 0.01%'
  //                         : `${percent.toFixed(2).replace(/\.?0+$/, '')} %`}
  //                     </span>
  //                   </>
  //                 ) : (
  //                   <>
  //                     <span className={s.percent_title}>
  //                       {t('applicationsReport.table.count.total')}
  //                     </span>
  //                     <span className={s.percent_count}>
  //                       {totalNumberLaunches}
  //                     </span>
  //                   </>
  //                 )}
  //               </div>
  //             </div>
  //           }
  //         >
  //           <ProgressBar
  //             className={s.progressBar}
  //             total={type !== 'CLICK' ? 100 : totalNumberLaunches}
  //             used={type !== 'CLICK' ? percent : numberLaunches}
  //           />
  //         </Tooltip>
  //       ),
  //       disableResizing: false,
  //     },
  //     {
  //       id: 'time',
  //       accessor: 'time',
  //       Header:
  //         type === EType.CLICK
  //           ? t('applicationsReport.table.count')
  //           : t('applicationsReport.table.time'),
  //       Cell: ({
  //         row: {
  //           original: { percent, time, numberLaunches },
  //         },
  //       }: {
  //         row: {
  //           original: Pick<IData, 'percent' | 'time' | 'numberLaunches'>;
  //         };
  //       }) => (
  //         <div className={s.right}>
  //           {type !== 'CLICK' ? (
  //             <>
  //               <span style={{ width: timeWidth }} className={s.right_time}>
  //                 {Math.floor(time / 3600) > 0
  //                   ? `${Math.floor(time / 3600)} ч.`
  //                   : '<1 ч.'}
  //               </span>
  //               <span className={s.right_percent}>
  //                 {percent < 0.01
  //                   ? '< 0.01%'
  //                   : `${percent.toFixed(2).replace(/\.?0+$/, '')} %`}
  //               </span>
  //             </>
  //           ) : (
  //             <span className={s.right_time}>{numberLaunches}</span>
  //           )}
  //         </div>
  //       ),
  //       width: size.width > 960 ? 319 : 148,
  //       disableResizing: true,
  //     },
  //   ],
  //   [t, type, data]
  // );
};
